import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import {
  Box,
  Button,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
} from '@mui/material';
import objectAssign from 'object-assign';
import PropTypes from 'prop-types';
import React, { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { useUser } from '@sophocles/app';
import ContextBreadcrumbs from '@sophocles/components/App/ContextBreadcrumbs';
import ErrorBoundary from '@sophocles/components/App/ErrorBoundary';
import LeftNav, { SIDEBAR_WIDTH_EXPANDED, SIDEBAR_WIDTH_COLLAPSED } from '@sophocles/components/App/LeftNav';
import LoadingScreen from '@sophocles/components/LoadingScreen';
import UserAvatar from '@sophocles/components/UserAvatar';
import { selectTenantInfo } from '@sophocles/features/tenant/slice';
import useBreadcrumbs from '@sophocles/hooks/useBreadcrumbs';

const NAV_TRANSITION = 'margin 450ms cubic-bezier(0.23, 1, 0.32, 1) 0ms, left 450ms cubic-bezier(0.23, 1, 0.32, 1) 0ms';

function AppNav({
  flex = false,
  loading,
  page,
  notFullHeight,
  title,
  notice,
  hideNav = false,
  showBack = false,
  children,
}) {
  const [isOpen, setIsOpen] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });

  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { t } = useTranslation();
  const tenantInfo = useSelector(selectTenantInfo);
  const breadcrumbs = useBreadcrumbs();
  // Overlay-nav cutoff (custom `mobile` = 768px). Must match LeftNav's own
  // `isPhone` breakpoint so the two never disagree about persistent-vs-temporary
  // drawer.
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('mobile'));
  const { user } = useUser();
  const [accountAnchor, setAccountAnchor] = useState(null);
  // Set by the search cards when drilling into a result — surfaces a
  // one-tap "Results" return path that doesn't require opening the nav.
  const cameFromSearch = Boolean(routerLocation.state?.fromSearch);

  useEffect(() => {
    setIsOpen(!hideNav && !isMobile);
    if (isMobile) {
      setIsCollapsed(false);
    }
  }, [hideNav, isMobile]);

  // Allow other components to set the LeftNav's collapsed state by
  // dispatching `window.dispatchEvent(new CustomEvent('sidebarCollapsedSet', { detail: false }))`.
  // The user-settings page uses this to force-expand the
  // LeftNav so its settings-mode section list is visible (the
  // sections only render when the LeftNav is expanded).
  useEffect(() => {
    const onSet = e => {
      if (typeof e.detail === 'boolean') {
        setIsCollapsed(e.detail);
      }
    };
    window.addEventListener('sidebarCollapsedSet', onSet);
    return () => window.removeEventListener('sidebarCollapsedSet', onSet);
  }, []);

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', isCollapsed.toString());
  }, [isCollapsed]);

  useEffect(() => {
    document.title = title ? title + ` | ${tenantInfo.organization_name}` : tenantInfo.organization_name;
  }, [title, tenantInfo.organization_name]);

  const getLeftMargin = () => {
    if (isMobile) return 0;
    if (!isOpen) return 0;
    return isCollapsed ? `${SIDEBAR_WIDTH_COLLAPSED}px` : `${SIDEBAR_WIDTH_EXPANDED}px`;
  };
  const leftMargin = getLeftMargin();

  let defaultStyle = {
    paddingTop: isMobile ? 'env(safe-area-inset-top)' : undefined,
    paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    transition: NAV_TRANSITION,
    height: isMobile ? '100dvh' : '100vh',
    overflowY: 'auto',
    boxSizing: 'border-box',
    backgroundColor: 'var(--app-content-bg)',
  };

  if (flex) {
    defaultStyle = {
      paddingTop: isMobile ? 'env(safe-area-inset-top)' : undefined,
      paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
      display: 'flex',
      position: 'relative',
      flexDirection: 'column',
      transition: NAV_TRANSITION,
      maxHeight: isMobile ? '100dvh' : '100vh',
      height: isMobile ? '100dvh' : '100vh',
      overflow: 'hidden',
      boxSizing: 'border-box',
      backgroundColor: 'var(--app-content-bg)',
    };
  }

  const style = notFullHeight
    ? objectAssign(defaultStyle, {
        marginLeft: leftMargin,
      })
    : objectAssign(defaultStyle, {
        bottom: 0,
        top: 0,
        right: 0,
        left: leftMargin,
        position: 'absolute',
      });

  return (
    <Fragment>
      {loading ? <LoadingScreen /> : null}
      <LeftNav
        open={isOpen}
        collapsed={isCollapsed}
        page={page}
        onClose={() => setIsOpen(false)}
        onOpen={() => setIsOpen(true)}
        onToggle={() => setIsCollapsed(!isCollapsed)}
        onExpand={() => setIsCollapsed(false)}
      />
      <div style={style}>
        {isMobile && !hideNav && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 1.5,
              py: 1,
              color: 'text.primary',
              flexShrink: 0,
              minHeight: 48,
            }}
          >
            {/* Unambiguous hamburger — opens the nav drawer. (Previously the
                brand logo doubled as the menu trigger, which read as a static
                logo with no hint it was tappable.) */}
            <IconButton
              size="small"
              onClick={() => setIsOpen(true)}
              aria-label={t('general.menu', 'Open menu')}
              sx={{ flexShrink: 0, borderRadius: 1.5, p: 0.5 }}
            >
              <MenuIcon sx={{ fontSize: 24 }} />
            </IconButton>
            {showBack && (
              <IconButton size="small" onClick={() => navigate(-1)} sx={{ color: 'inherit', ml: -0.5 }}>
                <ArrowBackIcon />
              </IconButton>
            )}
            {cameFromSearch && (
              <Button
                size="small"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/search')}
                sx={{ color: 'inherit', textTransform: 'none', flexShrink: 0, minWidth: 0, ml: -0.5 }}
              >
                {t('search.results', 'Results')}
              </Button>
            )}
            <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
              {breadcrumbs.length > 0 ? (
                <ContextBreadcrumbs
                  dense
                  crumbs={
                    title && title !== breadcrumbs[breadcrumbs.length - 1]?.label
                      ? [...breadcrumbs, { label: title }]
                      : breadcrumbs
                  }
                />
              ) : title ? (
                <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600, color: 'inherit' }}>
                  {title}
                </Typography>
              ) : null}
            </Box>
            {/* Global device/ICCID search — inset/elevated outlined button
                (the app's raised-pad style via MuiButton) at the header's
                trailing edge. */}
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate('/search')}
              aria-label={t('search.navLabel', 'Search')}
              sx={{ minWidth: 0, px: 1, flexShrink: 0, color: 'text.secondary' }}
            >
              <SearchIcon fontSize="small" />
            </Button>
            {/* Account avatar + quick menu — keeps identity and the common
                account actions reachable without opening the drawer. */}
            <IconButton
              size="small"
              onClick={e => setAccountAnchor(e.currentTarget)}
              aria-label={t('userSettings.account', 'Account')}
              sx={{ flexShrink: 0, p: 0.25 }}
            >
              <UserAvatar
                email={user.get('email', '')}
                firstName={user.get('first_name', '')}
                lastName={user.get('last_name', '')}
                avatarUrl={user.get('avatar')}
                size={30}
              />
            </IconButton>
            <Menu
              anchorEl={accountAnchor}
              open={Boolean(accountAnchor)}
              onClose={() => setAccountAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem
                onClick={() => {
                  setAccountAnchor(null);
                  navigate('/user');
                }}
              >
                <ListItemIcon>
                  <SettingsRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('general.settings', 'Settings')}</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setAccountAnchor(null);
                  navigate('/logout');
                }}
              >
                <ListItemIcon>
                  <LogoutRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('general.signOut', 'Sign out')}</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        )}
        {notice}
        <ErrorBoundary>{children}</ErrorBoundary>
      </div>
    </Fragment>
  );
}

AppNav.propTypes = {
  children: PropTypes.element,
  title: PropTypes.string,
};

export default AppNav;
